import React, { startTransition, useCallback, useEffect, useMemo, useState } from 'react';
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  ButtonBase,
  Button,
  CircularProgress,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Android as AndroidIcon,
  Apple as AppleIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  NotificationsNoneOutlined as NotificationsIcon,
  Search as SearchIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { notificationsApi } from '../features/notifications/api';
import { storage } from '../shared/lib/storage';
import { APP_LINKS } from '../shared/constants/appLinks';
import { formatEnumLabel } from '../shared/constants/enums';
import { normalizeRole, ROLES } from '../shared/constants/roles';
import { SUPPORT_CATEGORY_LABELS, SUPPORT_STATUS_LABELS } from '../shared/constants/support';

const DRAWER_WIDTH = 280;
const MINI_WIDTH = 76;
const APP_STORE_LINK_SX = {
  width: 40,
  height: 40,
  border: 1,
  borderColor: 'divider',
  bgcolor: 'background.paper',
  color: 'text.primary',
  boxShadow: 'none',
  flexShrink: 0,
  '&:hover': {
    bgcolor: 'secondary.light',
    borderColor: 'secondary.main',
    boxShadow: 'none',
  },
};

const SUPPORT_NOTIFICATION_TYPES = new Set([
  'SUPPORT_TICKET_CREATED',
  'SUPPORT_TICKET_REPLY',
  'SUPPORT_TICKET_STATUS_CHANGED',
]);

const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value, key);
const getTrimmedString = (value) => (typeof value === 'string' ? value.trim() : '');
const getReadableLabel = (labels, value) => labels[value] || formatEnumLabel(value);

const getSupportNotificationAction = (notification, metadata) => {
  switch (notification.type) {
    case 'SUPPORT_TICKET_CREATED':
      return 'created';
    case 'SUPPORT_TICKET_REPLY':
      return notification.title === 'Internal support note' ? 'internal note added' : 'new reply';
    case 'SUPPORT_TICKET_STATUS_CHANGED': {
      const status = getTrimmedString(metadata.status);
      return status ? `moved to ${getReadableLabel(SUPPORT_STATUS_LABELS, status)}` : '';
    }
    default:
      return '';
  }
};

const getSupportNotificationContext = (notification, role) => {
  const metadata = notification?.metadata;
  if (
    normalizeRole(role) !== ROLES.SUPER_ADMIN ||
    notification?.entityType !== 'SUPPORT_TICKET' ||
    !SUPPORT_NOTIFICATION_TYPES.has(notification?.type) ||
    !metadata ||
    typeof metadata !== 'object' ||
    !hasOwn(metadata, 'societyId') ||
    !hasOwn(metadata, 'societyName') ||
    !hasOwn(metadata, 'issueTitle') ||
    !hasOwn(metadata, 'issueCategory')
  ) {
    return null;
  }

  const societyName = metadata.societyName;
  const societyId = metadata.societyId;
  const ticketNumber = getTrimmedString(metadata.ticketNumber);
  const issueTitle = getTrimmedString(metadata.issueTitle);
  const issueCategory = getTrimmedString(metadata.issueCategory);
  const isSharedScope = societyName === null && societyId === null;
  const isScopedSociety = Boolean(getTrimmedString(societyName) && getTrimmedString(societyId));
  const action = getSupportNotificationAction(notification, metadata);

  if ((!isSharedScope && !isScopedSociety) || !ticketNumber || !issueTitle || !issueCategory || !action) {
    return null;
  }

  return {
    societyLabel: isSharedScope ? 'Shared scope' : societyName.trim(),
    ticketNumber,
    issueTitle,
    issueCategory: getReadableLabel(SUPPORT_CATEGORY_LABELS, issueCategory),
    action,
  };
};

const NotificationMenuContent = ({ notification, role }) => {
  const context = getSupportNotificationContext(notification, role);

  if (!context) {
    return (
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: notification.isRead ? 600 : 800 }}>
          {notification.title}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {notification.body}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
      <Typography
        variant="caption"
        color="primary.main"
        sx={{ display: 'block', fontWeight: 700, overflowWrap: 'anywhere' }}
      >
        {context.societyLabel}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', overflowWrap: 'anywhere' }}
      >
        {context.issueCategory} · {context.ticketNumber}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', overflowWrap: 'anywhere' }}
      >
        Action: {context.action}
      </Typography>
      <Box
        data-testid="support-notification-issue-title"
        title={context.issueTitle}
        sx={{ mt: 0.25, minWidth: 0 }}
        style={{
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: 2,
          overflow: 'hidden',
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: notification.isRead ? 600 : 800 }}>
          {context.issueTitle}
        </Typography>
      </Box>
    </Box>
  );
};

const AppShell = ({
  title,
  subtitle,
  navItems = [],
  user,
  role,
  onLogout,
  contextLabel,
  contextControl,
  settingsPath,
  onSearch,
  children,
}) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mini, setMini] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [notificationUnreadCount, setNotificationUnreadCount] = useState(0);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [openNavGroups, setOpenNavGroups] = useState({});
  const location = useLocation();
  const navigate = useNavigate();

  const effectiveMini = isDesktop && mini;
  const drawerWidth = useMemo(
    () => (isDesktop ? (effectiveMini ? MINI_WIDTH : DRAWER_WIDTH) : DRAWER_WIDTH),
    [effectiveMini, isDesktop],
  );
  const userInitials = useMemo(() => {
    const name = user?.fullName?.trim();
    if (!name) {
      return 'P';
    }

    return name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() || '')
      .join('');
  }, [user?.fullName]);

  const handleDrawerToggle = () => {
    if (isDesktop) {
      setMini((prev) => !prev);
    } else {
      setMobileOpen((prev) => !prev);
    }
  };

  const handleNav = (path) => {
    if (location.pathname === path) {
      return;
    }

    const navigationEvent = new CustomEvent('parktek:navigation-request', {
      cancelable: true,
      detail: { path },
    });
    window.dispatchEvent(navigationEvent);
    if (navigationEvent.defaultPrevented) {
      return;
    }

    startTransition(() => {
      navigate(path);
    });

    if (!isDesktop) {
      setMobileOpen(false);
    }
  };

  const isNavItemActive = (item) => {
    if (location.pathname === item.path) {
      return true;
    }

    if (item.match?.some((path) => location.pathname.startsWith(path))) {
      return true;
    }

    return item.children?.some((child) => isNavItemActive(child)) || false;
  };

  const handleNavItemClick = (item, isActive) => {
    if (item.children?.length && !effectiveMini) {
      setOpenNavGroups((prev) => ({
        ...prev,
        [item.label]: prev[item.label] === undefined ? !isActive : !prev[item.label],
      }));
      return;
    }

    handleNav(item.path);
  };

  const handleMenuOpen = (event) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const loadNotificationCount = useCallback(async () => {
    if (!storage.getToken()) {
      setNotificationUnreadCount(0);
      return;
    }

    try {
      const response = await notificationsApi.getUnreadCount();
      setNotificationUnreadCount(Number(response.data?.data?.unreadCount) || 0);
    } catch {
      setNotificationUnreadCount(0);
    }
  }, []);

  const loadNotifications = useCallback(async () => {
    setNotificationsLoading(true);
    try {
      const response = await notificationsApi.list({ page: 1, limit: 8 });
      const payload = response.data?.data || {};
      setNotifications(Array.isArray(payload.notifications) ? payload.notifications : []);
      setNotificationUnreadCount(Number(payload.unreadCount) || 0);
    } catch {
      setNotifications([]);
    } finally {
      setNotificationsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNotificationCount();
    const intervalId = window.setInterval(loadNotificationCount, 60000);
    return () => window.clearInterval(intervalId);
  }, [loadNotificationCount]);

  const handleNotificationsOpen = (event) => {
    setNotificationAnchor(event.currentTarget);
    loadNotifications();
  };

  const handleNotificationsClose = () => {
    setNotificationAnchor(null);
  };

  const handleNotificationClick = async (notification) => {
    if (!notification?.id) {
      return;
    }

    try {
      await notificationsApi.markRead(notification.id);
      setNotificationUnreadCount((current) => Math.max(current - (notification.isRead ? 0 : 1), 0));
    } catch {
      // Ignore read sync failures; navigation still gives user the target context.
    }

    const route =
      notification?.metadata?.route ||
      (notification?.entityType === 'SUPPORT_TICKET' && notification?.entityId
        ? `/support?ticketId=${encodeURIComponent(notification.entityId)}`
        : '');

    handleNotificationsClose();
    if (route) {
      handleNav(route);
    }
  };

  const handleMarkAllNotificationsRead = async () => {
    try {
      await notificationsApi.markAllRead();
      setNotificationUnreadCount(0);
      setNotifications((current) => current.map((item) => ({ ...item, isRead: true })));
    } catch {
      // No-op; next poll refreshes badge.
    }
  };

  const handleSettingsMenuClick = () => {
    handleMenuClose();
    handleNav(settingsPath);
  };

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box
        sx={{
          minHeight: 73,
          px: effectiveMini ? 0 : 1.5,
          py: 1.5,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {effectiveMini ? (
          <Box
            component="img"
            src="/brand-mark.svg"
            alt="ParkTek"
            sx={{ width: 30, height: 30, objectFit: 'contain' }}
          />
        ) : (
          <Box
            component="img"
            src="/logo.png"
            alt="ParkTek Business Console"
            sx={{ width: 140, maxWidth: '100%', height: 'auto', objectFit: 'contain' }}
          />
        )}
      </Box>
      <Divider />

      <List sx={{ display: 'flex', flexDirection: 'column', gap: 1, px: 1.5, py: 1.5 }}>
        {navItems.map((item) => {
          const isActive = isNavItemActive(item);
          const hasChildren = Boolean(item.children?.length);
          const childListOpen = hasChildren && (openNavGroups[item.label] ?? isActive) && !effectiveMini;

          const button = (
            <ListItemButton
              key={item.label}
              selected={isActive}
              onClick={() => handleNavItemClick(item, isActive)}
              sx={{
                justifyContent: effectiveMini ? 'center' : 'flex-start',
                minHeight: effectiveMini ? 42 : 42,
                px: effectiveMini ? 0 : 1.5,
                mx: 0,
                gap: effectiveMini ? 0 : 1,
                '& .MuiListItemText-primary': {
                  fontSize: 16,
                  fontWeight: isActive ? 700 : 500,
                  lineHeight: '20px',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: effectiveMini ? 0 : 16,
                  '& svg': { fontSize: 16 },
                }}
              >
                {item.icon}
              </ListItemIcon>
              {!effectiveMini && <ListItemText primary={item.label} />}
              {!effectiveMini && hasChildren ? (
                childListOpen ? (
                  <ExpandLessIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                ) : (
                  <ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                )
              ) : null}
            </ListItemButton>
          );

          if (effectiveMini) {
            return (
              <Tooltip title={item.label} placement="right" key={item.label}>
                {button}
              </Tooltip>
            );
          }

          return (
            <Box key={item.label}>
              {button}
              {hasChildren ? (
                <Collapse in={childListOpen} timeout="auto" unmountOnExit>
                  <List disablePadding sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {item.children.map((child) => {
                      const isChildActive = isNavItemActive(child);

                      return (
                        <ListItemButton
                          key={child.label}
                          selected={isChildActive}
                          onClick={() => handleNav(child.path)}
                          sx={{
                            minHeight: 36,
                            ml: 2.5,
                            pl: 1.5,
                            pr: 1.5,
                            gap: 1,
                            borderLeft: 0,
                            '& .MuiListItemText-primary': {
                              fontSize: 14,
                              fontWeight: isChildActive ? 700 : 500,
                              lineHeight: '18px',
                            },
                          }}
                        >
                          {child.icon ? (
                            <ListItemIcon
                              sx={{
                                minWidth: 18,
                                color: isChildActive ? 'text.primary' : 'text.secondary',
                                '& svg': { fontSize: 16 },
                              }}
                            >
                              {child.icon}
                            </ListItemIcon>
                          ) : null}
                          <ListItemText primary={child.label} />
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Collapse>
              ) : null}
            </Box>
          );
        })}
      </List>

      <Box sx={{ flexGrow: 1 }} />

      <Box sx={{ px: 1.5, pb: 1.5 }}>
        {effectiveMini ? (
          <Tooltip title="Logout" placement="right">
            <IconButton
              onClick={onLogout}
              aria-label="logout"
              sx={{
                width: 40,
                height: 40,
                mx: 'auto',
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                bgcolor: 'background.paper',
                color: 'error.main',
              }}
            >
              <LogoutIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          <ButtonBase
            onClick={onLogout}
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              borderRadius: 2,
              px: 2,
              py: 1.5,
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: 'background.paper',
              color: 'error.main',
              minHeight: 40,
            }}
          >
            <LogoutIcon sx={{ fontSize: 18 }} />
            <Typography
              variant="body2"
              sx={{ color: 'error.main', fontWeight: 500, fontSize: 14, lineHeight: '18px' }}
            >
              Logout
            </Typography>
          </ButtonBase>
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { lg: `calc(100% - ${drawerWidth}px)` },
          ml: { lg: `${drawerWidth}px` },
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.standard,
          }),
        }}
      >
        <Toolbar
          sx={{
            minHeight: 72,
            gap: 3,
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            alignItems: 'center',
            px: { xs: 1.5, sm: 3 },
            py: { xs: 1, sm: 0 },
          }}
          aria-label={`${title || 'ParkTek'} navigation`}
        >
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            aria-label="toggle navigation"
            sx={{
              mr: 0,
              width: 40,
              height: 40,
              borderRadius: 2,
              bgcolor: 'secondary.light',
              color: 'primary.main',
              '&:hover': {
                bgcolor: 'secondary.light',
              },
            }}
          >
            <MenuIcon />
          </IconButton>

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              flex: '1 1 auto',
              width: { xs: 'calc(100% - 64px)', sm: 'auto' },
              minWidth: 0,
              maxWidth: '100%',
              justifyContent: 'space-between',
            }}
          >
            {contextControl
              ? contextControl
              : contextLabel && (
                  <ButtonBase
                    sx={{
                      width: { xs: '100%', sm: 'auto' },
                      minWidth: { sm: 0 },
                      minHeight: 40,
                      justifyContent: 'center',
                      px: 2,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 2,
                      color: 'text.primary',
                      bgcolor: 'background.paper',
                      fontSize: 14,
                      fontWeight: 500,
                      lineHeight: '18px',
                    }}
                  >
                    {contextLabel}
                  </ButtonBase>
                )}

            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
              <Tooltip title="Open ParkTek on Google Play">
                <IconButton
                  component="a"
                  href={APP_LINKS.android}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open ParkTek on Google Play"
                  title="Open ParkTek on Google Play"
                  sx={APP_STORE_LINK_SX}
                >
                  <AndroidIcon fontSize="small" sx={{ width: 22, height: 22, display: 'block' }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Open ParkTek on App Store">
                <IconButton
                  component="a"
                  href={APP_LINKS.ios}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open ParkTek on App Store"
                  title="Open ParkTek on App Store"
                  sx={APP_STORE_LINK_SX}
                >
                  <AppleIcon fontSize="small" sx={{ width: 22, height: 22, display: 'block' }} />
                </IconButton>
              </Tooltip>
            </Stack>

            <Box sx={{ flexGrow: 1 }} />
            {subtitle ? (
              <Typography
                variant="body2"
                sx={{
                  display: { xs: 'none', sm: 'block' },
                  color: 'text.secondary',
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                }}
              >
                {subtitle}
              </Typography>
            ) : null}

            {onSearch ? (
              <Tooltip title="Search">
                <IconButton
                  color="inherit"
                  onClick={onSearch}
                  aria-label="Open global search"
                  sx={{ flexShrink: 0, width: 40, height: 40, borderRadius: 2 }}
                >
                  <SearchIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}

            <Tooltip title="Notifications">
              <IconButton
                color="inherit"
                onClick={handleNotificationsOpen}
                aria-label="Open notifications"
                sx={{
                  flexShrink: 0,
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                }}
              >
                <Badge color="error" badgeContent={notificationUnreadCount} max={99}>
                  <NotificationsIcon fontSize="small" />
                </Badge>
              </IconButton>
            </Tooltip>

            <IconButton
              color="inherit"
              onClick={handleMenuOpen}
              sx={{
                flexShrink: 0,
                width: 40,
                height: 40,
                borderRadius: '50%',
                p: 0,
              }}
            >
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: 'primary.main',
                  color: 'secondary.main',
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                {userInitials}
              </Avatar>
            </IconButton>
          </Stack>

          <Menu
            anchorEl={notificationAnchor}
            open={Boolean(notificationAnchor)}
            onClose={handleNotificationsClose}
            PaperProps={{ sx: { width: 360, maxWidth: 'calc(100vw - 24px)' } }}
          >
            <Box sx={{ px: 1.5, py: 1.25 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Notifications
                </Typography>
                <Button
                  size="small"
                  onClick={handleMarkAllNotificationsRead}
                  disabled={notificationUnreadCount === 0}
                >
                  Mark all read
                </Button>
              </Stack>
            </Box>
            <Divider />
            {notificationsLoading ? (
              <Box sx={{ py: 4, display: 'grid', placeItems: 'center' }}>
                <CircularProgress size={20} />
              </Box>
            ) : notifications.length === 0 ? (
              <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  No notifications
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Support updates will appear here.
                </Typography>
              </Box>
            ) : (
              notifications.map((notification) => (
                <MenuItem
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  sx={{
                    alignItems: 'flex-start',
                    gap: 1,
                    py: 1.25,
                    whiteSpace: 'normal',
                    bgcolor: notification.isRead ? 'inherit' : 'action.selected',
                  }}
                >
                  <NotificationMenuContent notification={notification} role={role || user?.role} />
                </MenuItem>
              ))
            )}
          </Menu>

          <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
            {user?.email && (
              <MenuItem disabled>
                <Typography variant="body2">{user.email}</Typography>
              </MenuItem>
            )}
            {settingsPath && (
              <MenuItem onClick={handleSettingsMenuClick}>
                <ListItemIcon>
                  <SettingsIcon fontSize="small" />
                </ListItemIcon>
                Settings
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                handleMenuClose();
                if (onLogout) onLogout();
              }}
            >
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Drawer
        variant={isDesktop ? 'permanent' : 'temporary'}
        open={isDesktop || mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: isDesktop ? drawerWidth : 'min(88vw, 320px)',
            boxSizing: 'border-box',
            overflowX: 'hidden',
            borderRight: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        {drawerContent}
      </Drawer>

      <Box
        component="main"
        sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}
      >
        <Toolbar sx={{ minHeight: '72px !important', flexShrink: 0 }} />
        <Box
          sx={{
            px: { xs: 1.5, sm: 2.5, lg: 3 },
            py: { xs: 1.5, sm: 2.5, lg: 3 },
            width: '100%',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default AppShell;
